import saveAs from 'file-saver';
import { useState } from 'react';
import { toast } from 'sonner';
import { useDownloadReportStatusContext } from '@/Context/UseDownloadReportStatusContext';
import { useAssessmentClient } from '@/Hooks/ClientHooks';
import { formatStringToYYYYMMDD } from '@/Utils/DateHelper';
import HttpError from '@/Utils/HTTP/Errors/HttpError';
import HttpStatusCodes from '@/Utils/HTTP/HttpStatusCodes';
import { useCandidatesResultsContext } from '../../Contexts/CandidateResultsContext';
import UIStrings from '@/Utils/UIStrings';

interface UseCandidateResultsHeaderInterface {
    isReportDownloading: boolean;
    downloadReportHandler: () => Promise<void>;
    openAssessmentResetDialog: boolean;
    setOpenAssessmentResetDialog: (value: boolean) => void;
}

function useCandidateResultsHeader(): UseCandidateResultsHeaderInterface {
    const assessmentClient = useAssessmentClient();
    const { results } = useCandidatesResultsContext();
    const { assessmentId, candidateId } = results;
    const [openAssessmentResetDialog, setOpenAssessmentResetDialog] = useState<boolean>(false);
    const { downloads, start, complete, getKey } = useDownloadReportStatusContext();

    const downloadReportHandler = async () => {
        start(assessmentId, candidateId);
        const { candidateFirstName, candidateLastName, completedAt } = results;

        toast.promise(
            async () => {
                const candidateIdBase64 = btoa(candidateId);
                const blob = await assessmentClient.getCandidateResultsPdfReport(assessmentId, candidateIdBase64);

                saveAs(
                    blob,
                    `${candidateFirstName}_${candidateLastName}_Applicant_Report_${formatStringToYYYYMMDD(
                        completedAt,
                    )}.pdf`,
                );
            },
            {
                loading: UIStrings.PdfDownloadPending(candidateFirstName),
                success: {
                    message: UIStrings.DownloadSuccessful,
                    description: UIStrings.PdfDownloadSuccess(candidateFirstName),
                },
                error: (e) => {
                    if (e instanceof HttpError && e.status === HttpStatusCodes.TooManyRequests) {
                        return { message: UIStrings.PdfDownloadWarning };
                    }

                    return {
                        message: UIStrings.DownloadFailed,
                        description: UIStrings.PdfDownloadFailure(candidateFirstName),
                    };
                },
            },
        );

        complete(assessmentId, candidateId);
    };

    return {
        isReportDownloading: downloads.has(getKey(assessmentId, candidateId)),
        downloadReportHandler,
        openAssessmentResetDialog,
        setOpenAssessmentResetDialog,
    };
}

export default useCandidateResultsHeader;
